<template>
  <v-layout column px-3 pb-3>
    <span xs12 class="display-1 pb-4">
      {{ $t('activity_reservation.title', { entityType: isRoomService ? $t('activity_reservation.order') : $t('activity_reservation.reservation').toLowerCase() }) }}
    </span>
    <v-flex class="app-scroll-section">
      <v-text-field
        v-if="guest"
        class="title grey--text text--darken-2 font-weight-light mt-4"
        :label="$t('fields.in_whose_name')"
        outlined
        readonly
        hide-details
        :value="getGuestFullName(guest)"
      />
      <v-text-field
        class="title grey--text text--darken-2 font-weight-light mt-4"
        :label="$t('fields.requested_date')"
        outlined
        readonly
        hide-details
        :value="requestedMoment.format('DD/MM/YYYY HH:mm')"
      />
      <!-- nominal attendees -->
      <v-flex xs12 v-if="nominalAttendees.length" class="grey--text text--darken-2 font-weight-light mt-4">
        <v-autocomplete
          :label="$t('fields.attendees')"
          :items="guests"
          :value="nominalAttendees"
          item-value="guest.id"
          append-icon=""
          hide-details
          outlined
          readonly
          multiple
        >
          <template v-slot:selection="{ item }">
            <v-chip>
              {{ getGuestFullName(item) }}
            </v-chip>
          </template>
        </v-autocomplete>
      </v-flex>
      <!-- numeral attendees -->
      <v-flex xs12 v-if="numeralAttendees.length">
        <v-flex
          v-for="(attendeeType, i) in attendeeTypes"
          :key="i"
          xs12>
          <v-text-field
            :label="$t('activity_reservation.attendee_type', { type: attendeeType.name })"
            :value="numeralAttendees.find((numeralAttendee) => numeralAttendee.id === attendeeType.id)
              ? numeralAttendees.find((numeralAttendee) => numeralAttendee.id === attendeeType.id).quantity
              : null"
            class="grey--text text--darken-2 font-weight-light mt-4"
            hide-details
            readonly
            outlined />
        </v-flex>
      </v-flex>
      <v-text-field
        v-if="room"
        class="title grey--text text--darken-2 font-weight-light mt-4"
        :label="$t('fields.room')"
        outlined
        readonly
        hide-details
        :value="room.room.name"
      />
      <div xs12 class="display-1 mt-4" style="width: 100%">
        {{ $t("activity_reservation.details") }}
      </div>
      <div class="mt-4">
        <order-summary
          v-if="orderLines.length > 0"
          :orderLines="orderLines"
          :totalByCurrencies="totalByCurrencies"
        />
        <span v-else xs12 class="" style="width: 100%">
          {{ $t("catalog.empty_cart") }}
        </span>
      </div>
      <v-textarea
        class="mt-4"
        :label="$t('fields.comments')"
        :placeholder="$t(`reservations.comments.${reservationMethod.type}`)"
        hide-details
        filled
        v-model="comments"
        :disabled="pendingConfirmation"
      >
      </v-textarea>
    </v-flex>
    <v-alert :value="orderIsUnpayable || isActiveAccountRequired || getCapacityStatus({ over: true })" dense type="error" color="red" text class="mt-3">
      {{ orderIsUnpayable ? $t('error.not_enough_balance') : isActiveAccountRequired ? $t('reservations.inactive_account') : $t("activity_reservation.over_capacity") }}
    </v-alert>
    <v-btn v-if="!getCapacityStatus({ over: true })"
      block
      outlined
      color="primary"
      class="primary-gradient mt-4"
      @click="completeReservation()"
      :disabled="orderIsUnpayable || isActiveAccountRequired || isProductNeeded"
      :loading="pendingConfirmation"
    >
      <v-layout row pa-5 align-center>
        <v-icon dark color="primary">shopping_cart</v-icon>
        <v-badge
          inline
          color="secondary"
          :content="amountInCart || '0'"
          class="ma-2"
        ></v-badge>
        <v-spacer />
        <span>{{ isRoomService ? $t("catalog.confirm_order") : $t("catalog.do_payment") }}</span>
      </v-layout>
    </v-btn>
    <v-btn
      v-else
      block
      outlined
      @click="exit()"
      color="grey"
      class="mt-4">
      {{ $t(`activity_reservation.actions.back.${reservationMethod.type}`) }}
    </v-btn>
  </v-layout>
</template>

<script>
import moment from 'moment'
import router from '@/router'
import routeNames from '@/router/routes'
import { mapGetters, mapState } from 'vuex'
import activityMixins from '@/mixins/Activity'
import reservationMixins from '@/mixins/Reservation'
import { getGuestFullName } from '@/lib/helpers/GuestName'
import { reservationTypes } from '@/config'

export default {
  beforeRouteEnter (to, from, next) {
    if (
      from.name === routeNames.reservationCommonData.name ||
      from.name === routeNames.reservationCatalog.name ||
      from.name === routeNames.catalogCategorySelection.name
    ) {
      next()
    } else {
      next({ name: routeNames.main.name })
    }
  },
  data () {
    return {
      moment,
      routeNames,
      pendingConfirmation: false,
      getGuestFullName,
    }
  },
  created () {
    if (!this.activityReservations.edited.activityId) {
      this.$router.push({ name: routeNames.reservationCommonData.name })
    }
    this.$store.dispatch('me/getBookings')
    this.$store.dispatch('activities/getActivity', {
      id: this.$route.params.activityId,
    })
  },
  methods: {
    exit () {
      router.push({
        name: routeNames.reservationCommonData.name,
        params: {
          categoryId: this.$route.params.categoryId,
          activityId: this.$route.params.activityId,
        },
      })
    },
    async completeReservation () {
      this.pendingConfirmation = true
      await this.getSlotCapacity()
      if (this.getCapacityStatus({ over: true })) return
      var order = this.orderLines.length > 0
        ? this.getOrder(this.reservationOrderLines)
        : null
      this.$store
        .dispatch('activityReservations/makeReservation', order)
        .then(() => {
          this.$router.push({ name: routeNames.reservationComplete.name })
        })
        .catch((error) => {
          this.$store.commit('state/setError', error)
          this.$router.push({ name: routeNames.reservationError.name })
        })
        .finally(() => {
          this.pendingConfirmation = false
        })
    },
  },
  computed: {
    isRoomService () {
      return this.reservationMethod.type === reservationTypes.ROOM_SERVICE
    },
    ...mapGetters({
      getOrder: 'cart/getOrder',
      guests: 'me/guests',
    }),
    ...mapState([
      'activities',
      'state',
      'activityReservations',
      'cart',
      'accounts',
    ]),
  },
  components: {
    OrderSummary: () => import('@/components/view-components/catalog/OrderSummary.vue'),
  },
  mixins: [
    activityMixins,
    reservationMixins,
  ],
}
</script>

<style lang="scss">
.notranslate {
  transform: none !important;
}
.section-width {
  max-width: 800px !important;
}
</style>
